import { storage } from '@abyss/web/tools/storage';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Constants } from '../../../../common/Constants';
import { convertUrlParamsToObject } from '../../../../common/Utils';
import { I18nextProvider, i18n } from '../../../../common/Utils/i18n/i18n';
import { PortalContext } from '../../../../context/PortalContext';
import { useSupportedLanguages } from '../../../../hooks/useSupportedLanguages';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { IChildrenProp } from '../../types';
import {
  getLanguage,
  getLanguageFromCookie,
  initializeLanguage,
} from './helpers';

const GLOBAL_NAV_LANG_POLL_MS = 500;

export const Internationalization = ({ children }: IChildrenProp) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { portalData } = useContext(PortalContext);
  const isLoadedRef = useRef(false);

  const urlParams = new URLSearchParams(window.location.search);
  const { locale: urlLocale, lang: urlLang }: any = convertUrlParamsToObject(
    urlParams.entries()
  );
  // Support both 'locale' and 'lang' params for backward compatibility
  const languageParam = urlLocale || urlLang;

  const supportedLanguages: string[] = useSupportedLanguages();

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { setLocale } = chipStore;

  useEffect(() => {
    const language = initializeLanguage(null, languageParam);
    i18n.changeLanguage(language.locale);
    setLocale(language.locale);
    isLoadedRef.current = true;
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    const applyLocaleIfChanged = () => {
      if (!isLoadedRef.current) return;
      const detectedLocale = getLanguageFromCookie();
      if (detectedLocale && detectedLocale !== i18n.language) {
        const language = getLanguage(detectedLocale);
        i18n.changeLanguage(language.locale);
        storage.cookie.set(Constants.PSLOCALE, language.locale);
        setLocale(language.locale);
      }
    };

    const intervalId = window.setInterval(
      applyLocaleIfChanged,
      GLOBAL_NAV_LANG_POLL_MS
    );

    return () => {
      window.clearInterval(intervalId);
    };
  }, [setLocale]);

  useEffect(() => {
    if (portalData?.portalConfig?.language) {
      selectlangaugeFromPortalData(portalData);
    }
  }, [portalData?.portalConfig?.language]);

  const selectlangaugeFromPortalData = (portalData) => {
    if (portalData?.portalConfig?.language) {
      const language = initializeLanguage(portalData, null);
      i18n.changeLanguage(language.locale);
      storage.cookie.set(Constants.PSLOCALE, language.locale);
      storage.session.set(
        Constants.STORAGE_KEYS.SESSION.SUPPORTED_LANGUAGES,
        JSON.stringify(Array.from(supportedLanguages))
      );
      setLocale(language.locale);
      setIsLoaded(true);
    }
  };

  if (!isLoaded) return null;

  return (
    <I18nextProvider i18n={i18n}>
      <Fragment>{children}</Fragment>
    </I18nextProvider>
  );
};
